import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import "./Stepper.css";

export type StepStatus = "future" | "active" | "done" | "error";

export interface StepperStep {
  label: ReactNode;
  status?: StepStatus;
  /** Optional number/glyph to render in the circle. Falls back to the 1-indexed position. */
  glyph?: ReactNode;
}

export interface StepperProps extends HTMLAttributes<HTMLOListElement> {
  steps: StepperStep[];
  orientation?: "horizontal" | "vertical";
  /** Index (0-based) of the currently active step. Use this to derive statuses if you don't set them per-step. */
  current?: number;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

const Check = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12.5l5 5L20 6.5" />
  </svg>
);

const ErrorIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v6" />
    <path d="M12 16.5h.01" />
  </svg>
);

export const Stepper = forwardRef<HTMLOListElement, StepperProps>(function Stepper(
  { steps, orientation = "horizontal", current, className, ...rest },
  ref,
) {
  return (
    <ol
      ref={ref}
      className={cx(
        "go4-stepper",
        orientation === "vertical" && "go4-stepper--vertical",
        className,
      )}
      style={{ listStyle: "none", padding: 0, margin: 0 }}
      {...rest}
    >
      {steps.map((step, idx) => {
        const status: StepStatus =
          step.status ??
          (current == null
            ? "future"
            : idx < current
              ? "done"
              : idx === current
                ? "active"
                : "future");
        return (
          <li
            key={idx}
            className={cx("go4-stepper__step", status === "done" && "go4-stepper__step--done")}
            aria-current={status === "active" ? "step" : undefined}
          >
            <span className={cx("go4-stepper__circle", `go4-stepper__circle--${status}`)}>
              {status === "done" ? (
                <Check />
              ) : status === "error" ? (
                <ErrorIcon />
              ) : (
                (step.glyph ?? idx + 1)
              )}
            </span>
            <span className={cx("go4-stepper__label", `go4-stepper__label--${status}`)}>
              {step.label}
            </span>
            <span aria-hidden="true" className="go4-stepper__connector" />
          </li>
        );
      })}
    </ol>
  );
});
